import { useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { MarketList, MarketRow } from './types.ts';

type Editing = { mode: 'new' } | { mode: 'edit'; market: MarketRow };

export function Markets() {
  const changes = useChanges();
  const list = useOp<MarketList>('admin.markets.list', {}, changes);
  const rows = list.data?.rows ?? [];
  const [cursor, setCursor] = useState(0);
  const [editing, setEditing] = useState<Editing | null>(null);
  const canEdit = list.data?.canEdit ?? false;

  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      enter: () => rows[cursor] && setEditing({ mode: 'edit', market: rows[cursor]! }),
      o: () => rows[cursor] && setEditing({ mode: 'edit', market: rows[cursor]! }),
      n: () => canEdit && setEditing({ mode: 'new' }),
    },
    editing === null,
  );

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Markets</h1>
          <p className="muted">Countries the catalog covers. Merchants, categories and sites belong to one market.</p>
        </div>
        {canEdit ? (
          <button type="button" className="btn btn-primary" onClick={() => setEditing({ mode: 'new' })}>
            New market <kbd className="kbd">n</kbd>
          </button>
        ) : null}
      </header>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {rows.length ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Country</th>
              <th scope="col">Locale</th>
              <th scope="col">Currency</th>
              <th scope="col">Time zone</th>
              <th scope="col" className="num">Merchants</th>
              <th scope="col" className="num">Categories</th>
              <th scope="col" className="num">Sites (yours)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.countryCode} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => setEditing({ mode: 'edit', market: row })} onMouseEnter={() => setCursor(index)}>
                <td>
                  <button type="button" className="link-button strong" onClick={() => setEditing({ mode: 'edit', market: row })}>
                    {row.countryCode} · {row.name}
                  </button>
                  {row.status !== 'active' ? <span className="badge badge-muted">inactive</span> : null}
                </td>
                <td>{row.locale}</td>
                <td>{row.currency}</td>
                <td className="muted">{row.timeZone}</td>
                <td className="num">{row.merchants}</td>
                <td className="num">{row.categories}</td>
                <td className="num">
                  {row.sites} <span className="muted">({row.mySites})</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {editing ? <MarketDrawer editing={editing} canEdit={canEdit} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

function MarketDrawer({ editing, canEdit, onClose }: { editing: Editing; canEdit: boolean; onClose: () => void }) {
  const toast = useToast();
  const market = editing.mode === 'edit' ? editing.market : null;
  const [form, setForm] = useState({
    countryCode: market?.countryCode ?? '',
    name: market?.name ?? '',
    locale: market?.locale ?? '',
    currency: market?.currency ?? '',
    timeZone: market?.timeZone ?? '',
    status: market?.status ?? 'active',
  });
  const [saving, setSaving] = useState(false);
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const result = market
      ? await runWrite<{ changed: string[] }>(toast, 'admin.markets.update', form, (r) => (r.changed.length ? `${form.countryCode} saved` : 'Nothing changed'))
      : await runWrite(toast, 'admin.markets.create', form, `${form.countryCode.toUpperCase()} added`);
    setSaving(false);
    if (result) onClose();
  };

  return (
    <Drawer title={market ? `${market.countryCode} · ${market.name}` : 'New market'} onClose={onClose}>
      <form onSubmit={submit}>
        {!canEdit ? <p className="muted">Changing markets needs an owner or admin role.</p> : null}
        <div className="form-grid">
          <label>
            Country code
            <input value={form.countryCode} onChange={set('countryCode')} maxLength={2} placeholder="DE" disabled={Boolean(market) || !canEdit} required autoFocus={!market} />
          </label>
          <label>
            Name
            <input value={form.name} onChange={set('name')} placeholder="Germany" disabled={!canEdit} required />
          </label>
          <label>
            Locale
            <input value={form.locale} onChange={set('locale')} placeholder="de-DE" disabled={!canEdit} required />
          </label>
          <label>
            Currency
            <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} placeholder="EUR" maxLength={3} disabled={!canEdit} required />
          </label>
          <label className="span-2">
            Time zone
            <input value={form.timeZone} onChange={set('timeZone')} placeholder="Europe/Berlin" disabled={!canEdit} required list="time-zones" />
            <datalist id="time-zones">
              {(typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []).map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </label>
          <label>
            Status
            <select value={form.status} onChange={set('status')} disabled={!canEdit}>
              <option value="active">Active</option>
              <option value="inactive">Inactive (no new merchants or sites)</option>
            </select>
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={!canEdit || saving}>
            {saving ? 'Saving…' : market ? 'Save' : 'Add market'}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
